<template>
  <div class="settings-screen" data-testid="settings-screen">
    <BzScreen :title="$t('message.settings')" :back-label="$t('message.back')" back-testid="settings-back"
              scroll-testid="settings-scroll" @back="goBack">
      <template #actions>
        <BzButton kind="secondary" icon="cloud_upload" :aria-label="$t('message.uploadQueue')"
                  data-testid="settings-open-queue" @click="$emit('open-queue-screen')"/>
      </template>

      <!-- Who this tablet is, as both systems open their settings: the account first. -->
      <div class="settings-identity">
        <span class="settings-identity__avatar"><BzIcon name="storefront" /></span>
        <span class="settings-identity__text">
          <span class="settings-identity__name" data-testid="settings-restaurant" translate="no">{{ restaurant.name }}</span>
          <span class="settings-identity__device">{{ $t('message.deviceId') }} <span data-testid="settings-device-id">{{ deviceId }}</span></span>
        </span>
      </div>

      <BzListGroup :title="$t('message.general')">
        <BzListRow action icon="campaign" data-testid="settings-changelog"
                   :label="$t('message.changelogTitle')" :hint="$t('message.changelogSettingsHint')"
                   @click="$emit('open-changelog')"/>
      </BzListGroup>

      <BzListGroup :title="$t('message.camera')">
        <!-- First of the group: «Cómo hacer buenas fotos», when the app's language has its video
             (src/photoVideoTutorial.js, docs/tutorial-video-buenas-fotos.md). -->
        <BzListRow v-if="photoVideo" action icon="play_circle" data-testid="settings-photo-video"
                   :label="$t('message.photoVideoTutorialSettings')"
                   :hint="$t('message.photoVideoTutorialSettingsHint', { duration: photoVideoDuration })"
                   @click="$emit('open-photo-video-tutorial')"/>
        <BzListRow v-if="isTablet" for-id="settings-immersive-input" icon="fullscreen" data-testid="settings-immersive"
                   :label="$t('message.immersiveViewport')" :hint="$t('message.immersiveViewportHint')">
          <template #control="{ labelId, hintId }">
            <BzToggle input-id="settings-immersive-input" :labelled-by="labelId" :described-by="hintId"
                      :model-value="settings.immersiveViewport !== false"
                      @update:model-value="updateSetting('immersiveViewport', $event)"/>
          </template>
        </BzListRow>
        <BzListRow for-id="settings-flip-input" icon="swap_vert" data-testid="settings-flip"
                   :label="$t('message.flipVertically')" :hint="$t('message.flipVerticallyHint')">
          <template #control="{ labelId, hintId }">
            <BzToggle input-id="settings-flip-input" :labelled-by="labelId" :described-by="hintId"
                      :model-value="Boolean(settings.flipVertically)"
                      @update:model-value="updateSetting('flipVertically', $event)"/>
          </template>
        </BzListRow>
        <BzListRow action icon="help" data-testid="settings-periscope-tutorial"
                   :label="$t('message.periscopeTutorialSettings')" :hint="$t('message.periscopeTutorialSettingsHint')"
                   @click="$emit('open-periscope-tutorial')"/>
        <BzListRow v-if="availableCameras && availableCameras.length > 1" icon="photo_camera" data-testid="settings-camera"
                   :label="$t('message.camera')" :hint="$t('message.cameraHint')">
          <template #control="{ labelId, hintId }">
            <BzSelect :labelled-by="labelId" :described-by="hintId" :options="cameraOptions"
                      :model-value="settings.selectedCameraId || ''"
                      @update:model-value="updateSetting('selectedCameraId', $event || null)"/>
          </template>
        </BzListRow>
        <BzListRow icon="hd" data-testid="settings-photo-resolution"
                   :label="$t('message.cameraPhotoResolution')" :hint="$t('message.cameraPhotoResolutionHint')">
          <template #control="{ labelId, hintId }">
            <BzSelect :labelled-by="labelId" :described-by="hintId" :options="photoResolutionOptions"
                      :model-value="photoResolutionValue"
                      @update:model-value="updateSetting('resolution', $event)"/>
          </template>
        </BzListRow>
        <BzListRow icon="camera" data-testid="settings-capture-method"
                   :label="$t('message.cameraCaptureMethod')" :hint="$t('message.cameraCaptureMethodHint')">
          <template #control="{ labelId, hintId }">
            <BzSelect :labelled-by="labelId" :described-by="hintId" :options="captureMethodOptions"
                      :model-value="captureMethodValue"
                      @update:model-value="updateSetting('cameraCaptureMethod', $event)"/>
          </template>
        </BzListRow>
        <!-- Black image detection. Default ON (detection runs unless explicitly disabled). Kept as a
             toggle in case a device produces false positives in production and we need to disable
             detection without shipping a new build. -->
        <BzListRow for-id="settings-black-image-input" icon="hide_image" data-testid="settings-black-image"
                   :label="$t('message.blackImageDetection')" :hint="$t('message.blackImageDetectionHint')">
          <template #control="{ labelId, hintId }">
            <BzToggle input-id="settings-black-image-input" :labelled-by="labelId" :described-by="hintId"
                      :model-value="settings.blackImageDetection !== false"
                      @update:model-value="updateSetting('blackImageDetection', $event)"/>
          </template>
        </BzListRow>
        <!-- Exposure Compensation — the one advanced knob that actually helps readability on dim tickets. -->
        <BzListRow icon="exposure" data-testid="settings-exposure"
                   :label="$t('message.cameraExposureComp')" :hint="$t('message.cameraExposureCompHint')">
          <template #control>
            <span class="slider-value">{{ exposureCompDisplay }}</span>
          </template>
          <template #below="{ labelId }">
            <BzSlider class="settings-slider" centered :labelled-by="labelId"
                      :min="exposureCompRange.min" :max="exposureCompRange.max" :step="exposureCompRange.step"
                      :model-value="settings.cameraExposureComp ?? 0"
                      @update:model-value="updateSetting('cameraExposureComp', roundToStep($event))" />
            <span class="slider-labels" aria-hidden="true">
              <span>{{ exposureCompRange.min }}</span>
              <span>0</span>
              <span>+{{ exposureCompRange.max }}</span>
            </span>
          </template>
        </BzListRow>
      </BzListGroup>

      <!-- Deleito's clients and Bronze's own only: the Bluetooth QR gun's guide (docs/tutorial-pistola-qr.md). -->
      <BzListGroup v-if="showQrGunTutorial" :title="$t('message.qrGunTutorialGroup')">
        <BzListRow action icon="help" data-testid="settings-qr-gun-tutorial"
                   :label="$t('message.qrGunTutorialSettings')" :hint="$t('message.qrGunTutorialSettingsHint')"
                   @click="$emit('open-qr-gun-tutorial')"/>
      </BzListGroup>

      <BzListGroup :title="$t('message.settingsAdvanced')">
        <BzListRow action icon="bug_report" data-testid="settings-debug"
                   :label="$t('message.debug')" :hint="$t('message.debugHint')" @click="showDebugScreen"/>
      </BzListGroup>

      <!-- Bronze's own client only (src/liveDemo/flag.js): the live sales demo. -->
      <BzListGroup v-if="demoAllowed" :title="$t('message.demoMode')">
        <BzListRow for-id="settings-demo-mode-input" icon="touch_app" data-testid="settings-demo-row"
                   :label="$t('message.demoMode')" :hint="demoHint">
          <template #control="{ labelId, hintId }">
            <BzToggle :key="demoToggleKey" input-id="settings-demo-mode-input"
                      :labelled-by="labelId" :described-by="hintId"
                      :model-value="demoOn"
                      @update:model-value="setDemoMode($event)"/>
          </template>
        </BzListRow>
        <BzListRow v-if="demoOn" action icon="refresh" data-testid="settings-demo-restart"
                   :label="$t('message.demoRestart')" :hint="$t('message.demoRestartHint')"
                   @click="askDemoCategory(demoCategory)"/>
      </BzListGroup>

      <!-- The one destructive action, alone in its group, as Android and iOS put it. -->
      <BzListGroup>
        <BzListRow action tone="danger" icon="logout" data-testid="settings-signout"
                   :label="$t('message.signOut')" :hint="$t('message.signOutHint')" @click="signOut"/>
      </BzListGroup>

      <p class="settings-version" data-testid="settings-version">{{ $t('message.version') }} {{ applicationVersion }} · {{ $t('message.madeWithLove') }}</p>
    </BzScreen>

    <!-- The one destructive action asks in the system's dialog, not the browser's: a confirm() comes up
         white, in the system font and saying which website asks, over an app that is meant to look
         installed. -->
    <BzDialog card :visible="signOutAsked" band="modal"
              :title="$t('message.confirmSignOut')"
              :close-label="$t('message.close')"
              mask-testid="settings-signout-dialog"
              @dismiss="signOutAsked = false">
      <template #footer>
        <div class="settings-signout-actions">
          <BzButton size="lg" kind="secondary" :label="$t('message.cancel')"
                    data-testid="settings-signout-cancel" @click="signOutAsked = false"/>
          <BzButton size="lg" kind="danger" :label="$t('message.signOut')"
                    data-testid="settings-signout-confirm" @click="confirmSignOut"/>
        </div>
      </template>
    </BzDialog>

    <LiveDemoCategoryPicker v-if="demoAllowed" :visible="demoPickerOpen" :selected="demoPickerSelected"
                            @pick="startDemo" @cancel="demoPickerOpen = false" />

    <DebugScreen v-if="debugScreenVisible" @go-back="hideDebugScreen" />
  </div>
</template>


<script>
import {signOut as amplifySignOut} from 'aws-amplify/auth';
import packageInfo from "../../package.json"
import Logger from '@/logger';
import DebugScreen from './DebugScreen.vue';
import BzButton from '@/ui/components/BzButton.vue';
import BzDialog from '@/ui/components/BzDialog.vue';
import BzIcon from '@/ui/components/BzIcon.vue';
import BzListGroup from '@/ui/components/BzListGroup.vue';
import BzListRow from '@/ui/components/BzListRow.vue';
import BzScreen from '@/ui/components/BzScreen.vue';
import BzSelect from '@/ui/components/BzSelect.vue';
import BzSlider from '@/ui/components/BzSlider.vue';
import BzToggle from '@/ui/components/BzToggle.vue';
import LiveDemoCategoryPicker from './LiveDemoCategoryPicker.vue';
import { categoryById } from '@/liveDemo/catalog';
import { liveDemoAllowed, liveDemoCategory, liveDemoOn, setLiveDemo } from '@/liveDemo/flag';
import { PHOTO_WIDTH_OPTIONS, DEFAULT_CAMERA_RESOLUTION } from './CameraPreviewComponent.vue';
import { formatClock } from '@/photoVideoTutorial';

const logger = new Logger({screen: 'SettingsScreen'});

export default {
  name: 'SettingsScreen',
  components: {
    DebugScreen,
    LiveDemoCategoryPicker,
    BzButton,
    BzDialog,
    BzIcon,
    BzListGroup,
    BzListRow,
    BzScreen,
    BzSelect,
    BzSlider,
    BzToggle,
  },
  props: ['cameraPermissions', 'settings', 'deviceId', 'restaurant', 'availableCameras', 'streamInfo', 'showQrGunTutorial', 'photoVideo'],
  data() {
    return {
      applicationVersion: packageInfo.version,
      debugScreenVisible: false,
      signOutAsked: false,
      isTablet: window.innerWidth >= 768,
      // BzListRow's phone width: the row's line holds the name and the value, so the value is shorter.
      // Read once, as isTablet: a phone turned sideways keeps the short value.
      isNarrow: window.innerWidth < 600,
      // Live sales demo (Bronze's own client only): is it running, and the category picker's state.
      demoOn: liveDemoOn(),
      demoPickerOpen: false,
      demoToggleKey: 0,
      demoPickerSelected: null,
    }
  },
  computed: {
    photoVideoDuration() {
      return formatClock(this.photoVideo && this.photoVideo.durationS);
    },
    demoAllowed() {
      return liveDemoAllowed(this.restaurant);
    },
    // The category the running demo was started with, as catalog.js resolves it.
    demoCategory() {
      return this.demoOn ? categoryById(liveDemoCategory()).id : null;
    },
    // The row's subtitle says which restaurant type is running while it is on.
    demoHint() {
      if (!this.demoOn) return this.$t('message.demoModeHint');
      return this.$t('message.demoActive', { category: this.$t(categoryById(this.demoCategory).labelKey) });
    },
    exposureCompRange() {
      // The backend enumerates exposure as the -3.0..3.0 grid in 0.1 steps. Constrain the slider to
      // the intersection of that canonical domain and the sensor's real range, on the 0.1 grid, so
      // the value the operator picks is exactly what gets persisted (no local-vs-backend drift).
      // The sensor's real range still clamps the *applied* exposure separately, at capture time.
      const sensor = this.parsedStreamCapabilities?.exposureCompensation;
      const min = sensor ? Math.max(sensor.min, -3) : -3;
      const max = sensor ? Math.min(sensor.max, 3) : 3;
      return { min, max, step: 0.1 };
    },
    exposureCompDisplay() {
      const val = this.settings.cameraExposureComp ?? 0;
      return val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1);
    },
    photoResolutionValue() {
      const raw = this.settings.resolution;
      return PHOTO_WIDTH_OPTIONS.includes(raw) ? raw : DEFAULT_CAMERA_RESOLUTION;
    },
    photoResolutionOptions() {
      // Build [{ value, label, short }] from the whitelist. Both show the qualitative name (Baja/Media/Alta)
      // and the pixel width, so the user always sees the concrete number they're picking; the menu adds
      // which one is recommended, the row shows the short one. On a phone the row's line is the name's and
      // the value's (iOS's pattern, 14-oct-2026): the row says the tier, the menu still says the pixels.
      return PHOTO_WIDTH_OPTIONS.map((w) => {
        let tier;
        if (w <= 1600) tier = this.$t('message.cameraPhotoResolutionLow');
        else if (w >= 2560) tier = this.$t('message.cameraPhotoResolutionHigh');
        else tier = this.$t('message.cameraPhotoResolutionMedium');
        const suffix = w === DEFAULT_CAMERA_RESOLUTION ? ` · ${this.$t('message.cameraPhotoResolutionRecommended')}` : '';
        return { value: w, label: `${tier} — ${w} px${suffix}`, short: this.isNarrow ? tier : `${tier} · ${w} px` };
      });
    },
    cameraOptions() {
      return [
        { value: '', label: this.$t('message.cameraDefault') },
        ...(this.availableCameras || []).map((cam) => ({ value: cam.deviceId, label: cam.label })),
      ];
    },
    // Two ways to capture, not three. The backend allows `auto` (its default, on most of the fleet),
    // `canvas` and `imageCapture` (bronze.setting_value), and gives none of them a meaning of its own; the
    // camera treats `auto` and `canvas` alike (CameraPreviewComponent: only `imageCapture` takes the
    // sensor's still). «Automatic» was a third choice that did nothing different. So the fast way is shown
    // once and writes `auto`, the default the backend seeds, and a `canvas` set by operations still reads
    // as the fast way. No stored value changes meaning and no new one is written.
    captureMethodOptions() {
      return [
        { value: 'auto', label: this.$t('message.cameraCaptureMethodCanvas'), short: this.$t('message.cameraCaptureMethodCanvasShort') },
        { value: 'imageCapture', label: this.$t('message.cameraCaptureMethodImageCapture'), short: this.$t('message.cameraCaptureMethodImageCaptureShort') },
      ];
    },
    captureMethodValue() {
      return this.settings.cameraCaptureMethod === 'imageCapture' ? 'imageCapture' : 'auto';
    },
    parsedStreamCapabilities() {
      if (!this.streamInfo?.capabilities) return null;
      try { return JSON.parse(this.streamInfo.capabilities); } catch (e) { return null; }
    },
  },
  methods: {
    signOut() {
      this.signOutAsked = true;
    },
    async confirmSignOut() {
      this.signOutAsked = false;
      try {
        await amplifySignOut();
      } catch (err) {
        logger.error('Error signing out', {error: err});
      }
      localStorage.removeItem('device_id'); // Clear stored device ID
      this.$emit('sign-out');
    },
    goBack() {
      this.$emit('go-back');
    },
    // The slider hands back what its arithmetic gives (0.30000000000000004); the native range
    // handed back its step, and the value is persisted and sent to the backend as it is. The
    // decimals come from the step itself, so a different step rounds right too.
    roundToStep(value) {
      const step = this.exposureCompRange.step;
      const decimals = (String(step).split('.')[1] || '').length;
      return Number((Math.round(value / step) * step).toFixed(decimals)) || 0;
    },
    updateSetting(setting, value) {
      const newSettings = {...this.settings, [setting]: value};
      this.$emit('update-settings', newSettings);
    },
    // The switch: «on» asks the category first (the switch stays off until one is picked; leaving the picker
    // changes nothing), «off» writes the flag and restarts the app, which gives the real orders back.
    setDemoMode(on) {
      if (on) {
        // The switch is controlled by the flag, which only the picker's answer writes: draw it off again.
        this.demoToggleKey += 1;
        this.askDemoCategory(null);
      } else this.storeDemo(false);
    },
    askDemoCategory(selected) {
      this.demoPickerSelected = selected;
      this.demoPickerOpen = true;
    },
    // A category was picked: boot reads the flag and its category, so this always starts the scenario from zero,
    // even when it was already running (the restart row). Nothing is sent.
    startDemo(categoryId) {
      this.demoPickerOpen = false;
      if (this.storeDemo(true, categoryId)) this.reloadAfterPicker();
    },
    // Writes the flag; false when the storage refused it (nothing to restart for).
    storeDemo(on, categoryId) {
      try {
        setLiveDemo(on, categoryId);
      } catch (e) {
        logger.error('Could not store the demo mode', {error: e});
        return false;
      }
      this.demoOn = on;
      if (!on) window.location.reload();
      return true;
    },
    // The picker took a history entry when it opened (src/ui/backClose.js) and takes it out again when it closes,
    // with an asynchronous history.go(-1) that cancels a reload requested meanwhile: wait for that popstate, and
    // reload anyway if it never comes.
    reloadAfterPicker() {
      let done = false;
      const reload = () => {
        if (done) return;
        done = true;
        window.removeEventListener('popstate', reload);
        window.location.reload();
      };
      window.addEventListener('popstate', reload);
      setTimeout(reload, 500);
    },
    showDebugScreen() {
      this.debugScreenVisible = true;
    },
    hideDebugScreen() {
      this.debugScreenVisible = false;
    },
  }

};
</script>


<style scoped>
/* Settings is on the design system (docs/sistema-diseno/README.md): its screen, rows and controls are
   the library's, and what is here is only the screen's own pieces. */
.settings-screen {
  position: absolute;
  inset: 0;
}

/* The tablet's identity: the store and the device, as the account card that opens both systems'
   settings — a card of the system (its surface, edge and corners). */
.settings-identity {
  display: flex;
  align-items: center;
  gap: var(--bz-space-4);
  margin: 0 0 var(--bz-space-7);
  padding: var(--bz-space-4);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
}

.settings-identity__avatar {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: var(--bz-target-md);
  height: var(--bz-target-md);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  color: var(--bz-ink);
  font-size: var(--bz-icon-md);
}

.settings-identity__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.settings-identity__name {
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
  overflow-wrap: anywhere;
}

.settings-identity__device {
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
  overflow-wrap: anywhere;
}

/* Sign out's two answers, side by side and of one size: Cancel and the red one. */
.settings-signout-actions {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: var(--bz-target-gap);
  width: 100%;
}

/* The exposure: its value next to the label, the slider and its scale under them. */
/* The value as a choice's value is written: plain, in the second ink — not a pill, which would be the
   one field left in the list (the control is the slider under it). */
.slider-value {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-body);
  font-variant-numeric: tabular-nums;
}

.settings-slider {
  margin-top: var(--bz-space-2);
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: var(--bz-space-1);
  font-size: var(--bz-text-xs);
  line-height: var(--bz-leading-xs);
  letter-spacing: var(--bz-tracking-xs);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
  font-variant-numeric: tabular-nums;
}

.settings-version {
  margin: 0;
  text-align: center;
  font-size: var(--bz-text-xs);
  line-height: var(--bz-leading-xs);
  letter-spacing: var(--bz-tracking-xs);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
}
</style>
